import { useCallback, useEffect, useMemo, useRef } from "react";
import useAuth from "./useAuth";
import useMutation from "./useMutation";
import { UPDATE_SELECTION_CONTEXT_MUTATION } from "../constants/graphql";

const STORAGE_KEY_MAP = {
  client_id: "clientId",
  project_id: "projectId",
  warehouse_id: "warehouseId",
};

const hasSelectionKey = (selectionContext, key) =>
  Object.prototype.hasOwnProperty.call(selectionContext || {}, key);

const getLegacyStorageValue = key => {
  const storageKey = STORAGE_KEY_MAP[key];
  if (!storageKey) return undefined;

  try {
    const value = localStorage.getItem(storageKey);
    return value === null ? undefined : value;
  } catch (_error) {
    return undefined;
  }
};

const EMPTY_SELECTION = Object.freeze({});
// After a failed write, wait this long before the same patch may be sent again;
// doubles per consecutive failure up to the max.
const FAILURE_BACKOFF_MS = 15000;
const FAILURE_BACKOFF_MAX_MS = 5 * 60 * 1000;

const buildMutationVariables = patch => ({
  clientId: patch.client_id,
  projectId: patch.project_id,
  warehouseId: patch.warehouse_id,
});

export default function useSelectionContext() {
  const { user, setUser } = useAuth();
  const [updateSelectionContextMutation] = useMutation(UPDATE_SELECTION_CONTEXT_MUTATION);
  const backfillAttemptedRef = useRef(false);
  // Memoized on the server value: a fresh `{}` per render would re-create every
  // callback below and re-fire the route-sync effects that depend on them.
  const rawSelectionContext = useMemo(
    () => user?.selectionContext || EMPTY_SELECTION,
    [user?.selectionContext]
  );

  useEffect(() => {
    backfillAttemptedRef.current = false;
  }, [user?.id]);

  const getSelectionValue = useCallback(
    key => {
      if (hasSelectionKey(rawSelectionContext, key)) {
        return rawSelectionContext[key];
      }

      return getLegacyStorageValue(key);
    },
    [rawSelectionContext]
  );

  const selectionContext = useMemo(
    () => ({
      client_id: getSelectionValue("client_id"),
      project_id: getSelectionValue("project_id"),
      warehouse_id: getSelectionValue("warehouse_id"),
    }),
    [getSelectionValue]
  );

  // Last patch sent to the server that has not resolved yet. Route-sync effects
  // (ProjectLayout) and page mounts re-run before the mutation response lands and
  // used to re-send the identical patch every time — ~40 writes a minute on prod.
  const inFlightRef = useRef(null);
  // Last write that failed (422/CSRF, network, validation). Callers re-run on
  // every render, so without this a doomed write is retried at network speed —
  // one stale tab produced ~12 failed writes a second on prod.
  const failureRef = useRef({ key: null, attempts: 0, until: 0, errors: null });

  const updateSelectionContext = useCallback(
    (patch, options = {}) => {
      const mutationPatch = Object.fromEntries(
        Object.entries(patch || {}).filter(
          ([key, value]) =>
            value !== undefined &&
            // Already persisted with this exact value: nothing to write.
            !(hasSelectionKey(rawSelectionContext, key) && String(rawSelectionContext[key]) === String(value))
        )
      );

      if (!user || Object.keys(mutationPatch).length === 0) {
        options.onSuccess?.(selectionContext);
        return Promise.resolve();
      }

      const patchKey = JSON.stringify(mutationPatch);
      const failure = failureRef.current;
      if (failure.key === patchKey && Date.now() < failure.until) {
        options.onFailure?.(failure.errors);
        return Promise.resolve();
      }
      if (inFlightRef.current?.key === patchKey) {
        return inFlightRef.current.promise.then(result => {
          options.onSuccess?.(result);
          return result;
        });
      }

      const promise = updateSelectionContextMutation({
        variables: buildMutationVariables(mutationPatch),
        onSuccess: ({ user: updatedUser }) => {
          failureRef.current = { key: null, attempts: 0, until: 0, errors: null };
          if (updatedUser) {
            setUser(updatedUser);
          }
          options.onSuccess?.(updatedUser?.selectionContext || mutationPatch);
        },
        onFailure: errors => {
          const previous = failureRef.current;
          const attempts = previous.key === patchKey ? previous.attempts + 1 : 1;
          const delay = Math.min(FAILURE_BACKOFF_MAX_MS, FAILURE_BACKOFF_MS * 2 ** (attempts - 1));
          failureRef.current = { key: patchKey, attempts, until: Date.now() + delay, errors };
          options.onFailure?.(errors);
        },
      });

      const tracked = Promise.resolve(promise)
        .then(response => Object.values(response?.data || {})[0]?.user?.selectionContext || mutationPatch)
        .catch(() => mutationPatch)
        .finally(() => {
          if (inFlightRef.current?.key === patchKey) inFlightRef.current = null;
        });
      inFlightRef.current = { key: patchKey, promise: tracked };

      return promise;
    },
    [rawSelectionContext, selectionContext, setUser, updateSelectionContextMutation, user]
  );

  useEffect(() => {
    if (!user || backfillAttemptedRef.current) return;

    const missingPatch = {};

    Object.keys(STORAGE_KEY_MAP).forEach(key => {
      if (hasSelectionKey(rawSelectionContext, key)) return;

      const fallbackValue = getLegacyStorageValue(key);
      if (fallbackValue !== undefined) {
        missingPatch[key] = fallbackValue;
      }
    });

    backfillAttemptedRef.current = true;

    if (Object.keys(missingPatch).length > 0) {
      updateSelectionContext(missingPatch);
    }
  }, [rawSelectionContext, updateSelectionContext, user]);

  return {
    selectionContext,
    rawSelectionContext,
    hasSelectionKey: key => hasSelectionKey(rawSelectionContext, key),
    getSelectionValue,
    updateSelectionContext,
    clientId: selectionContext.client_id,
    projectId: selectionContext.project_id,
    warehouseId: selectionContext.warehouse_id,
  };
}
